/**
 * SSE (Server-Sent Events) utility for real-time AI progress tracking.
 * Connects to the backend auto-configure-stream endpoint and receives
 * progress events as each AI layer completes.
 */

/**
 * Get the base URL for API calls.
 * Uses the same dynamic hostname logic as api.js so SSE works
 * regardless of which IP/network the user accesses from.
 */
function getApiBaseUrl() {
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  // In production (same port), use relative path
  if (window.location.port === '' || window.location.port === '80' || window.location.port === '443') {
    return '/api';
  }
  // In development, API is on port 5174
  return `${protocol}//${hostname}:5174/api`;
}

/**
 * Stream auto-configure progress via SSE.
 *
 * @param {string} formId - The form ID to auto-configure
 * @param {string|null} token - JWT token for temporary compatibility, optional when cookie auth is used
 * @param {function} onProgress - Called with each progress event: { layer, name, status, fieldsFound, error }
 * @param {function} onComplete - Called with the final configuration object when done
 * @param {function} onError - Called on connection error
 * @returns {function} cleanup - Call to close the SSE connection
 */
export function streamAutoConfigure(formId, token, onProgress, onComplete, onError, model = null, replaceExisting = false) {
  const baseUrl = getApiBaseUrl();
  const params = new URLSearchParams();
  if (token) params.set('token', token);
  if (model) params.set('model', model);
  if (replaceExisting) params.set('replaceExisting', 'true');
  const queryString = params.toString();
  const url = `${baseUrl}/admin/forms/${formId}/auto-configure-stream${queryString ? '?' + queryString : ''}`;

  const eventSource = new EventSource(url, { withCredentials: true });

  eventSource.addEventListener('progress', (e) => {
    try {
      const data = JSON.parse(e.data);
      onProgress(data);
    } catch (err) {
      console.error('[SSE] Failed to parse progress event:', err);
    }
  });

  eventSource.addEventListener('complete', (e) => {
    try {
      const data = JSON.parse(e.data);
      eventSource.close();
      onComplete(data);
    } catch (err) {
      console.error('[SSE] Failed to parse complete event:', err);
      eventSource.close();
      onError(err);
    }
  });

  eventSource.addEventListener('error', (e) => {
    // Check if this is a named error event from the server
    if (e.data) {
      try {
        const data = JSON.parse(e.data);
        console.error('[SSE] Server error:', data.error);
        eventSource.close();
        onError(new Error(data.error || 'Server error'));
        return;
      } catch (_) {
        // Not a JSON error event
      }
    }
    // Connection error
    eventSource.close();
    onError(new Error('SSE connection failed'));
  });

  // Also handle the generic onerror (connection drops, etc.)
  eventSource.onerror = () => {
    if (eventSource.readyState === EventSource.CLOSED) {
      // Already closed, ignore
      return;
    }
    eventSource.close();
    onError(new Error('SSE connection lost'));
  };

  // Return cleanup function
  return () => {
    eventSource.close();
  };
}
